import { useTranslation } from "react-i18next";
import {ScissorOutlined, DeleteOutlined, CloseOutlined, CheckOutlined} from "@ant-design/icons";
import {Select, Spin, Modal, Col, Form, Row, Space, Button, Input, DatePicker, InputNumber, Switch} from "antd";
import {useState, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {FamilyMemberCreateRequestDto} from "../api/client-axios";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {Problem} from "../model/problem";
import {deleteNoOrgFamilyMember, fetchNoOrgFamilyMembers} from "../features/familyMembersNoOrgSlice";
import {
    clearSaveNoOrgFamilyMemberError,
    fetchNoOrgFamilyMember,
    initNoOrgFamilyMemberState,
    saveNoOrgFamilyMember,
    updateNoOrgFamilyMemberFields, updateNoOrgFamilyMemberPhoneNumber
} from "../features/familyMemberNoOrgSlice";

const {Option} = Select;

export const EditNoOrgFamilyMemberPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const familyMembersStatus = useAppSelector((state: RootState) => state.familyMembersNoOrg.status);
    const familyMember = useAppSelector((state: RootState) => state.familyMemberNoOrg.familyMember);
    const status = useAppSelector((state: RootState) => state.familyMemberNoOrg.status);
    const error = useAppSelector((state: RootState) => state.familyMemberNoOrg.error);
    const countries = useAppSelector((state: RootState) => state.country.countries);


    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemoveFamilyMemberWarningVisible, setIsRemoveFamilyMemberWarningVisible] = useState(false);
    const [isDeleteFamilyMemberWarningVisible, setIsDeleteFamilyMemberWarningVisible] = useState(false);

    const familyMemberId = params.familyMemberId;

    useEffect(() => {
        if (familyMembersStatus === "idle") {
            dispatch(fetchNoOrgFamilyMembers({}));
        }
        dispatch(fetchNoOrgFamilyMember({familyMemberId: familyMemberId!}));
    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchNoOrgFamilyMembers({}));
            dispatch(initNoOrgFamilyMemberState());
            navigate(-1);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(-1);
    }

    const changeFamilyMember = (familyMember: FamilyMemberCreateRequestDto) => {
        dispatch(updateNoOrgFamilyMemberFields(familyMember));
    }

    const onSave = () => {
        dispatch(saveNoOrgFamilyMember({familyMemberId: familyMemberId!, familyMember: familyMember as FamilyMemberCreateRequestDto}));
    }


    const onDelete = () => {
        setIsDeleteFamilyMemberWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemoveFamilyMemberWarningVisible(true);
    }

    const onDeleteFamilyMember = () => {
        if (familyMember?.id != null) {
            dispatch(deleteNoOrgFamilyMember({familyMemberId: familyMember.id}));
        }
    }

    const onConfirm = (phoneNumber: string) => {
        dispatch(updateNoOrgFamilyMemberPhoneNumber(phoneNumber));
    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSaveNoOrgFamilyMemberError());
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }


    if (status === "idle" || status === "loading" || familyMembersStatus === "idle") {
        return <Spin/>;
    }

    if (status === "auth-error") {
        return <Modal title={t("error.auth")} visible={isModalVisible} onOk={handleAuthOk} onCancel={handleCancel}>
            <p>{t("error.authFailedLogin")}</p>
        </Modal>
    }

    if (status === "error") {
        const apiError = error as Problem;
        const title = apiError.title ?? t("error.server");
        const detail = apiError.detail ?? error?.toString;
        return <Modal title={title} visible={isModalVisible} onOk={handleErrorOk} onCancel={handleCancel}>
            <p>{detail}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }

    return (<div className='edit-person-main-frame'>
        <Col span={24}>
            <Form className='person-form'
                  name="basic"
                  labelCol={{span: 6}}
                  wrapperCol={{span: 24}}
                  initialValues={{remember: true}}
                //onFinish={onFinish}
                //onFinishFailed={onFinishFailed}
                  autoComplete="off"
            >

                <Row>
                    {/* <Col className='person-title' offset={6} span={11}>FamilyMember</Col> */}
                    <Col offset={6} span={18}>
                        <Form.Item>
                            <Row justify="end">
                                <Space className="mr-xxs">
                                    <Button icon={<DeleteOutlined/>} onClick={onDelete} type='primary' danger
                                            disabled={familyMember?.id === undefined}>{t("actions.delete")}</Button>
                                </Space>

                                <Space>
                                    <Button icon={<CloseOutlined/>} onClick={onCancel}>{t("actions.cancel")}</Button>
                                    <Button icon={<CheckOutlined/>} type='primary' onClick={onSave}>{t("actions.save")}</Button>
                                </Space>
                            </Row>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.phoneNumber")} rules={[{required: true}]}
                                   initialValue={familyMember?.userAccount?.mobileNumber}>
                            <Input disabled placeholder={t("placeholder.phoneNumber")}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.email")}>
                            <Input placeholder={t("placeholder.email")}
                                   defaultValue={familyMember?.userAccount?.emailAddress || ""} onChange={(e) => {
                                changeFamilyMember({...familyMember, email: e.target.value} as FamilyMemberCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.firstName")} rules={[{required: true, message: t("validation.firstNameRequired")}]}>
                            <Input placeholder={t("placeholder.firstName")} defaultValue={familyMember?.firstName || ""} onChange={(e) => {
                                changeFamilyMember({...familyMember, firstName: e.target.value} as FamilyMemberCreateRequestDto)
                            }}/>
                        </Form.Item>

                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.lastName")}>
                            <Input placeholder={t("placeholder.lastName")} defaultValue={familyMember?.lastName || ""} onChange={(e) => {
                                changeFamilyMember({...familyMember, lastName: e.target.value} as FamilyMemberCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.country")} initialValue={familyMember?.address?.country}>
                            <Select
                                onChange={(e) => changeFamilyMember({
                                    ...familyMember,
                                    address: {...familyMember?.address, alpha3Code: e}
                                } as FamilyMemberCreateRequestDto)}
                                defaultValue={familyMember?.address?.alpha3Code}
                                showSearch
                                style={{width: 200}}
                                placeholder={t("placeholder.searchToSelect")}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }}
                                filterSort={(optionA, optionB) =>
                                    (optionA!.children![2] as unknown as string)
                                        .toLowerCase()
                                        .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                                }
                            >
                                {countries.map(country => <Option key={country.alpha3Code}
                                                                  value={country.alpha3Code}><img src={country.flag!}
                                                                                                  width="20"></img> {country.name}
                                </Option>)}

                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={familyMember?.address?.streetAddress1 as string | undefined}
                                   placeholder={t("placeholder.addressLine1")} onChange={(e) => {
                                changeFamilyMember({
                                    ...familyMember,
                                    address: {...familyMember?.address, streetAddress1: e.target.value}
                                } as FamilyMemberCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={familyMember?.address?.streetAddress2 as string | undefined}
                                   placeholder={t("placeholder.addressLine2")} onChange={(e) => {
                                changeFamilyMember({
                                    ...familyMember,
                                    address: {...familyMember?.address, streetAddress2: e.target.value}
                                } as FamilyMemberCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.city")}>
                            <Input defaultValue={familyMember?.address?.city as string | undefined} placeholder={t("placeholder.city")}
                                   onChange={(e) => {
                                       changeFamilyMember({
                                           ...familyMember,
                                           address: {...familyMember?.address, city: e.target.value}
                                       } as FamilyMemberCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.state")}>
                            <Input defaultValue={familyMember?.address?.state as string | undefined} placeholder={t("placeholder.state")}
                                   onChange={(e) => {
                                       changeFamilyMember({
                                           ...familyMember,
                                           address: {...familyMember?.address, state: e.target.value}
                                       } as FamilyMemberCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.postcode")}>
                            <Input defaultValue={familyMember?.address?.postCode as string | undefined} placeholder={t("placeholder.postcode")}
                                   onChange={(e) => {
                                       changeFamilyMember({
                                           ...familyMember,
                                           address: {...familyMember?.address, postCode: e.target.value}
                                       } as FamilyMemberCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Col>
        <Modal visible={isDeleteFamilyMemberWarningVisible} okText={t("actions.yes")} cancelText={t("actions.no")}
               onCancel={() => setIsDeleteFamilyMemberWarningVisible(false)} onOk={onDeleteFamilyMember}>{t("confirm.delete.familyMember", { name: [familyMember?.firstName, familyMember?.lastName].filter(Boolean).join(" ") })}</Modal>
    </div>);
}